"use client";

import { useState, useEffect } from "react";
import { ArrowRight, ChevronDown, Menu, X } from "lucide-react";
import Link from "next/link";
import { ASSETS, TABS } from "@/constants";
import { ECHOPAY_SVG } from "@/assets/svgs";

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAssetsOpen, setIsAssetsOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <nav
      className={`w-full h-[96px] md:h-[96px] sm:h-20 ${
        isScrolled ? "fixed" : "relative"
      } top-0 left-0 right-0 z-50 transition-all duration-300 font-inter bg-[#FFFDFA80] backdrop-blur-sm shadow-md`}
    >
      <div className="max-w-[95rem] mx-auto px-4 sm:px-6 lg:px-8 h-full">
        <div className="flex items-center justify-between h-full">
          {/* Logo */}
          <Link
            href="/"
            className="flex items-center gap-2 hover:opacity-80 transition-opacity flex-shrink-0"
          >
            {ECHOPAY_SVG().resolvaOne()}
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex gap-4 lg:gap-8 items-center">
            {TABS.map((tab, key) => {
              if (tab.name === "Assets") {
                return (
                  <div
                    key={key}
                    className="relative"
                    onMouseEnter={() => setIsAssetsOpen(true)}
                    onMouseLeave={() => setIsAssetsOpen(false)}
                  >
                    <button
                      onClick={() => setIsAssetsOpen(!isAssetsOpen)}
                      className="w-full flex items-center gap-1 text-[#010721] font-medium text-xs lg:text-sm tracking-[-0.6px]"
                    >
                      Assets
                      <span
                        className={`transition-transform ${
                          isAssetsOpen ? "rotate-180" : ""
                        }`}
                      >
                        <ChevronDown size={17} />
                      </span>
                    </button>

                    <div
                      className={`absolute top-full -left-16 mt-3 w-52 rounded-xl bg-white shadow-xl border border-gray-100 transition-all duration-200 ${
                        isAssetsOpen
                          ? "opacity-100 visible translate-y-0"
                          : "opacity-0 invisible -translate-y-2"
                      }`}
                    >
                      {ASSETS.map((asset) => (
                        <Link
                          key={asset.name}
                          href={asset.link}
                          className="block px-5 py-3 text-sm text-[#010721] hover:bg-gray-50 first:rounded-t-xl last:rounded-b-xl"
                        >
                          {asset.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              }

              return (
                <Link
                  key={key}
                  href={tab.link}
                  target={tab.link === "/" ? "_parent" : "_blank"}
                  className="text-[#010721] font-medium text-xs lg:text-sm tracking-[-0.6px]"
                >
                  {tab.name}
                </Link>
              );
            })}
            <Link
              href="/login"
              className="text-[#010721] font-medium text-xs lg:text-sm tracking-[-0.6px] align-middle"
            >
              Log in
            </Link>
            <Link
              href="https://link.useresolva.io/download"
              target="_blank"
              className="bg-[#EAFA35] text-[#0046A7] font-medium text-xs lg:text-sm h-10 md:h-14 rounded-lg py-2 md:py-[10px] px-4 md:px-6 lg:px-[24px] flex gap-2 md:gap-[12px] items-center align-middle tracking-[-0.15px] flex-shrink-0 transform transition-all duration-200 ease-in-out translate-y-0 hover:-translate-y-1 hover:shadow-[0_5px_0_0_#C2D120] active:translate-y-0 active:shadow-none"
            >
              Get Resolva
              <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4" />
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden flex items-center justify-center w-10 h-10 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-[#010721]" />
            ) : (
              <Menu className="w-6 h-6 text-[#010721]" />
            )}
          </button>
        </div>
      </div>

      <div
        className={`fixed top-24 sm:top-20 right-0 h-screen w-full bg-white shadow-lg transform transition-transform duration-300 ease-in-out md:hidden z-40 ${
          isMobileMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex flex-col p-4 sm:p-6 gap-4 sm:gap-6">
          {TABS.map((tab, key) => {
            if (tab.name === "Assets") {
              return (
                <div key={key}>
                  <button
                    onClick={() => setIsAssetsOpen(!isAssetsOpen)}
                    className="w-full flex items-center justify-between text-[#010721] font-medium text-xs lg:text-sm tracking-[-0.6px] align-middle"
                  >
                    Assets
                    <span
                      className={`transition-transform ${
                        isAssetsOpen ? "rotate-180" : ""
                      }`}
                    >
                      <ChevronDown size={17} />
                    </span>
                  </button>

                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      isAssetsOpen ? "max-h-60 mt-2" : "max-h-0"
                    }`}
                  >
                    {ASSETS.map((asset) => (
                      <Link
                        key={asset.name}
                        href={asset.link}
                        onClick={handleNavClick}
                        className="block pl-5 py-2 text-xs lg:text-sm tracking-[-0.6px] text-gray-700"
                      >
                        {asset.name}
                      </Link>
                    ))}
                  </div>
                </div>
              );
            }

            return (
              <Link
                key={key}
                href={tab.link}
                target={tab.link === "/" ? "_parent" : "_blank"}
                onClick={handleNavClick}
                className="text-[#010721] font-medium text-xs lg:text-sm tracking-[-0.6px] align-middle"
              >
                {tab.name}
              </Link>
            );
          })}
          <Link
            href="/login"
            className="text-[#010721] font-medium text-xs lg:text-sm tracking-[-0.6px] align-middle"
          >
            Log in
          </Link>
          <Link
            href="https://link.useresolva.io/download"
            target="_blank"
            className="bg-[#EAFA35] hover:shadow-[0px_2px_7px_2px_rgba(0,84,140,0.75)] transition-all h-10 md:h-14 rounded-lg py-2 md:py-[10px] px-4 md:px-6 lg:px-[24px] flex gap-2 md:gap-[12px] items-center text-[#0046A7] font-medium text-xs lg:text-sm align-middle tracking-[-0.15px] flex-shrink-0"
            onClick={handleNavClick}
          >
            Get Resolva
            <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4" />
          </Link>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 md:hidden z-30 top-24 sm:top-20"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
    </nav>
  );
}
